import React from 'react';
import JSONBig from 'json-bigint';
import { SnackbarPublicProps, useSnackbar } from 'pounceV2';
import {
  AIInferenceStreamUsage,
  AIAuthorDetectionInputDetection,
  AIAuthorSkillInputSkill,
  AISummarizeKind,
  AISummarizeMetadata,
} from 'Generated/schema';
import { RerunFormValues } from './GenericAISummaryContextV3';
import { useAIStreamManagementV3, useAIMutationsV3, useAIStreamStateV3 } from './hooks';
import { useGetLatestAIStreamForCaching } from './graphql/getLatestAiStreamForCaching.generated';

const MAX_EVENTS_SIZE = 5 * 1024 * 1024; // stay far away from 6MB lambda limit

export type AISummaryStreamParams = {
  aiStream?: 'new-stream' | string;
  parentStreamId?: string;
};

type UseFetchGenericAiSummaryProps = {
  kind: AISummarizeKind;
  alias: string;
  // TODO: refactor this to remove events and detection and pass metadata instead
  events?: [Record<string, any>];
  detection?: AIAuthorDetectionInputDetection;
  skill?: AIAuthorSkillInputSkill;
  currentProfile?: string;
  metadata?: AISummarizeMetadata;
  syncStreamIdWithUrl?: boolean;
  onStreamComplete?: (summary: string, userPrompt: string, usage?: AIInferenceStreamUsage) => void;
  lastModified?: string;
  private?: boolean;
};

export const convertEventsToJsonStrings = (
  events: [Record<string, any>],
  pushSnackbar: (snackbar: SnackbarPublicProps) => void,
) => {
  let eventsSize = 0;
  const convertedEvents: string[] = [];
  for (let i = 0; i < events.length; i += 1) {
    const jsonEvent = JSONBig.stringify(events[i]);
    eventsSize += jsonEvent.length;
    if (eventsSize > MAX_EVENTS_SIZE) {
      pushSnackbar({
        variant: 'error',
        title: `Too many events to send for AI analysis, stopping at ${i}`,
      });
      break;
    }
    convertedEvents.push(jsonEvent);
  }
  return convertedEvents;
};

export default function useFetchGenericAiSummaryV3({
  kind,
  alias,
  events,
  detection,
  skill,
  currentProfile,
  metadata,
  syncStreamIdWithUrl = false,
  lastModified,
  onStreamComplete,
  private: isPrivate,
}: UseFetchGenericAiSummaryProps) {
  const { pushSnackbar } = useSnackbar();

  // Use a ref for currentProfile so the rerun callback stays stable across re-renders.
  // currentProfile only needs to be read at mutation time, not on every render.
  const currentProfileRef = React.useRef(currentProfile);
  currentProfileRef.current = currentProfile;

  // Convert events to JSON strings
  const jsonEvents = React.useMemo(() => {
    if (events) {
      return convertEventsToJsonStrings(events, pushSnackbar);
    }
    return [];
  }, [events, pushSnackbar]);

  // Fetch existing AI streams for this alias to check for cached response
  // Using a minimal query to avoid cache errors with optional fields
  const { data: existingStreamsData, loading: loadingExistingStreams } =
    useGetLatestAIStreamForCaching({
      variables: {
        input: {
          alias,
          pageSize: 1, // Only need the most recent one
        },
      },
      skip: !alias || !lastModified, // Only fetch if we have both alias and lastModified
      fetchPolicy: 'no-cache', // Completely bypass cache to avoid normalization errors
    });

  // Stream state management
  const { streamId, parentStreamId, isNewStream, setStreamId, clearStreamId } = useAIStreamStateV3({
    syncWithUrl: syncStreamIdWithUrl,
  });

  // Check if we have a valid cached AI response
  // Do not use cached responses that have a parentStreamId (drilldown responses)
  // Only evaluate after the query has completed (not while loading)
  const cachedStreamId = React.useMemo(() => {
    // Don't evaluate while query is still loading
    if (loadingExistingStreams) {
      return null;
    }

    if (!lastModified || !existingStreamsData?.aiInferenceStreamsMetadata?.edges?.length) {
      return null;
    }

    const latestStream = existingStreamsData.aiInferenceStreamsMetadata.edges[0]?.node;
    if (!latestStream) {
      return null;
    }

    // Do not use cached responses that have a parentStreamId (these are drilldown responses)
    if (latestStream.parentStreamId) {
      return null;
    }

    // Compare timestamps: use cached response if it was created after the reference was last modified
    const streamCreatedAt = new Date(latestStream.createdAt);
    const referenceLastModified = new Date(lastModified); // For example, a detection

    if (streamCreatedAt > referenceLastModified) {
      return latestStream.streamId;
    }

    return null;
  }, [lastModified, existingStreamsData, loadingExistingStreams]);

  // Set the stream ID to the cached one if available and no stream ID is set yet
  React.useEffect(() => {
    if (cachedStreamId && !streamId) {
      setStreamId(cachedStreamId);
    }
  }, [cachedStreamId, streamId, setStreamId]);

  // Stream management with polling
  const {
    aiSummary,
    aiSummaryError,
    initialUserPrompt,
    usage,
    isLoading: streamLoading,
    isCancelled,
    cancelStream,
    toolInvocations,
    riskScoreEvidence,
  } = useAIStreamManagementV3({
    streamId,
    onStreamComplete,
  });

  const [isLoading, setIsLoading] = React.useState(false);

  // Mutation management
  const { runAISummarize } = useAIMutationsV3({
    kind,
    alias,
    onStreamCreated: setStreamId,
    onError: () => {
      setIsLoading(false);
    },
  });

  // Sync the loading state with the stream loading state to avoid UI flickering.
  // This is necessary because the stream loading state is not updated immediately when the stream is created.
  React.useEffect(() => {
    setIsLoading(streamLoading);
  }, [streamLoading]);

  const rerun = React.useCallback(
    (values: RerunFormValues, attachments?: string[]) => {
      setIsLoading(true);
      // Determine parent stream ID for drilldown scenarios
      let effectiveParentStreamId = streamId;
      if (isNewStream) {
        effectiveParentStreamId = parentStreamId;
      }

      runAISummarize({
        alias,
        events: jsonEvents,
        metadata,
        prompt: values.additionalPrompt,
        outputLength: values.outputLength,
        detection,
        skill,
        currentProfile: currentProfileRef.current,
        parentStreamId: effectiveParentStreamId,
        private: isPrivate,
        attachments,
      });
    },
    [
      runAISummarize,
      detection,
      skill,
      alias,
      jsonEvents,
      metadata,
      streamId,
      isNewStream,
      parentStreamId,
      isPrivate,
    ],
  );

  const cancelPrompt = React.useCallback(() => {
    cancelStream();
  }, [cancelStream]);

  return {
    rerun,
    aiSummary,
    aiSummaryError,
    isLoading,
    cancelPrompt,
    isCancelled,
    initialUserPrompt,
    usage,
    checkingForCachedResponse: loadingExistingStreams,
    hasCachedResponse: !!cachedStreamId,
    toolInvocations,
    streamId,
    riskScoreEvidence,
    clearStream: clearStreamId,
  };
}
